/**
 * The permission strings the UI checks, mirroring DPL.Domain.Identity.Permissions.
 *
 * Only the ones the front end actually gates on — this is not a second copy of
 * the whole catalogue, which would drift. A test asserts every string here exists
 * on the server, so a rename cannot silently turn a nav item invisible (or, worse,
 * permanently visible) without failing the build.
 */
export const Permissions = {
  Dashboard: {
    View: 'dashboard.view',
    ViewAllLocations: 'dashboard.view.all',
    ViewProfit: 'dashboard.profit.view',
  },
  Product: {
    View: 'product.view',
    Create: 'product.create',
    Edit: 'product.edit',
    Deactivate: 'product.deactivate',
    Import: 'product.import',
    ViewCost: 'product.cost.view',
    EditPrice: 'product.price.edit',
  },
  Customer: {
    View: 'customer.view',
    Create: 'customer.create',
    Edit: 'customer.edit',
    Block: 'customer.block',
    ViewStatement: 'customer.statement.view',
    EditCreditLimit: 'customer.creditlimit.edit',
    ManageVatExemption: 'customer.vatexemption.manage',
  },
  Supplier: {
    View: 'supplier.view',
    Create: 'supplier.create',
    Edit: 'supplier.edit',
    Approve: 'supplier.approve',
  },
  Sales: {
    ViewRegister: 'sales.register.view',
    ViewProfit: 'sales.profit.view',
    PosSell: 'sales.pos.sell',
    OpenRegister: 'sales.pos.register.open',
    CloseRegister: 'sales.pos.register.close',
    ApproveDiscount: 'sales.discount.approve',
    OverrideMinimumPrice: 'sales.price.override',
    OverrideCreditLimit: 'sales.creditlimit.override',
    CreateCreditNote: 'sales.creditnote.create',
    ApproveCreditNote: 'sales.creditnote.approve',
    WaiveVat: 'sales.vat.waive',
  },
  Crm: {
    ViewLeads: 'crm.lead.view',
    ManageLeads: 'crm.lead.manage',
    LogInteraction: 'crm.interaction.log',
  },
  Loyalty: {
    Manage: 'loyalty.manage',
    Adjust: 'loyalty.adjust',
  },
  Support: {
    View: 'support.ticket.view',
    Manage: 'support.ticket.manage',
    Warranty: 'support.warranty.manage',
  },
  Purchasing: {
    View: 'purchase.view',
    ApproveOwn: 'purchase.approve.own',
    CreateRequisition: 'purchase.requisition.create',
    ApproveRequisition: 'purchase.requisition.approve',
    CreateOrder: 'purchase.order.create',
    ApproveOrder: 'purchase.order.approve',
    ReceiveGoods: 'purchase.grn.create',
    VerifyGoods: 'purchase.grn.verify',
    RecordInvoice: 'purchase.invoice.create',
    PaySupplier: 'purchase.payment.create',
  },
  Warehouse: {
    ViewStock: 'warehouse.stock.view',
    ViewAllLocations: 'warehouse.stock.view.all',
    CreateIndent: 'warehouse.indent.create',
    ApproveIndent: 'warehouse.indent.approve',
    CreateTransfer: 'warehouse.transfer.create',
    DispatchTransfer: 'warehouse.transfer.dispatch',
    ReceiveTransfer: 'warehouse.transfer.receive',
    Adjust: 'warehouse.adjustment.create',
    ApproveAdjustment: 'warehouse.adjustment.approve',
    StockTake: 'warehouse.stocktake.manage',
    ReleaseQuarantine: 'warehouse.quarantine.release',
    ManageReorder: 'warehouse.reorder.manage',

    // D-226: borrowing from the shops next door.
    RequestLoan: 'warehouse.loan.request',
    ApproveLoan: 'warehouse.loan.approve',
    IssueLoan: 'warehouse.loan.issue',
    CloseLoan: 'warehouse.loan.close',
    ManageAssociates: 'warehouse.associate.manage',
  },
  Finance: {
    ViewAccounts: 'finance.accounts.view',
    ManageAccounts: 'finance.accounts.manage',
    ViewReports: 'finance.reports.view',
    RecordPayment: 'finance.payment.create',
    PostJournal: 'finance.journal.post',
    LockPeriod: 'finance.period.lock',
    ApproveExpense: 'finance.expense.approve',
    PettyCashSpend: 'finance.pettycash.spend',
    PettyCashApprove: 'finance.pettycash.approve',
    ManageAssets: 'finance.asset.manage',
    OpeningBalances: 'finance.openingbalance.post',
    RecordReceipt: 'finance.receipt.create',
    ReverseReceipt: 'finance.receipt.reverse',
    Reconcile: 'finance.reconciliation.manage',
    ManageMpesa: 'finance.mpesa.manage',
    /** Change how a payment was made after the fact — the Owner and the Super Admin only (D-274). */
    ChangePaymentMethod: 'finance.payment.method.change',
  },
  Delivery: {
    ViewBoard: 'delivery.board.view',
    Create: 'delivery.create',
    AssignRider: 'delivery.rider.assign',
    UpdateStatus: 'delivery.status.update',
    CompleteOwn: 'delivery.own.complete',
    /** Shipping goods out to a customer through a courier (D-247). */
    Dispatch: 'delivery.dispatch',
    /** Keeping the list of couriers the business ships through (D-247). */
    ManageCouriers: 'delivery.courier.manage',
  },
  Logistics: {
    ViewShipments: 'logistics.shipment.view',
    ManageShipments: 'logistics.shipment.manage',
  },
  Reports: {
    View: 'reports.view',
    ViewProfitability: 'reports.profitability.view',
    Export: 'reports.export',
  },
  Administration: {
    ManageUsers: 'admin.users.manage',
    ManageRoles: 'admin.roles.manage',
    ViewAuditLog: 'admin.audit.view',
    ManageTemplates: 'admin.templates.manage',
    ManageSettings: 'admin.settings.manage',
    ManageTargets: 'admin.targets.manage',
    ManageLocations: 'admin.locations.manage',
  },
  Messaging: {
    View: 'messaging.view',
    ManageCampaigns: 'messaging.campaign.manage',
    ApproveCampaigns: 'messaging.campaign.approve',
  },
} as const

/** Flattened, for the test that checks these against the server's catalogue. */
export function allUiPermissions(): string[] {
  const found: string[] = []

  const walk = (node: Record<string, unknown>): void => {
    for (const value of Object.values(node)) {
      if (typeof value === 'string') found.push(value)
      else if (value && typeof value === 'object') walk(value as Record<string, unknown>)
    }
  }

  walk(Permissions)
  return found
}
